import React from 'react';
import { CardSectionRate } from '../../CardSectionRate/CardSectionRate';
import SectionSubtitle from '@/components/Typography/SectionSubtitle';
import { TextSummaryBox } from '@/components/TextSummaryBox/TextSummaryBox';
import { InspectionList } from '@/components/InspectionList';
import { InspectionItem } from '@/components/InspectionList/InspectionList.types';
import { RatingSection } from '@/api/mappers/mapper-types';
import { CardSectionRateWide, SectionShortcutButton, SectionSummary, SVGIcon } from '@/components';
import { PhotoGallery } from '@/components/PhotoGallery';
import type { PhotoGalleryItem } from '@/components/PhotoGallery/PhotoGallery.types';
import { SectionNoData } from '@/components/SectionNoData';

interface RaportEngineProps {
  /** Sekcja oceny komory silnika i bagażnika z tytułem, ratingiem i opisem */
  ratingSection: RatingSection;
  /** Lista uwag dotyczących ekspertyzy komory silnika i bagażnika */
  remarks: string[];
  /** Lista elementów komory silnika do weryfikacji */
  engineCompartmentItems: InspectionItem[];
  /** Podsumowanie eksperta dla komory silnika */
  engineExpertSummary?: string;
  /** Lista elementów komory bagażnika do weryfikacji */
  trunkCompartmentItems: InspectionItem[];
  /** Podsumowanie eksperta dla komory bagażnika */
  trunkExpertSummary?: string;
  /** Galeria zdjęć komory silnika */
  galleryPhotos?: PhotoGalleryItem[];
}

export default function RaportEngine({
  ratingSection,
  remarks,
  engineCompartmentItems,
  engineExpertSummary,
  trunkCompartmentItems,
  trunkExpertSummary,
  galleryPhotos,
}: RaportEngineProps) {
  const hasEngineShortcut = engineCompartmentItems.length > 0;
  const hasTrunkShortcut = trunkCompartmentItems.length > 0;
  const hasAnyShortcut = hasEngineShortcut || hasTrunkShortcut;

  return (
    <>
      {hasAnyShortcut && (
        <div className="px-4 mt-10 md:hidden">
          <p className="text-sm font-semibold text-text-primary mb-4">
            Sprawdź raporty dostępne w tej sekcji:
          </p>
          <div className="flex flex-col gap-3">
            {hasEngineShortcut && (
              <SectionShortcutButton
                label="Komora silnika"
                targetId="stan-komory-silnika"
              />
            )}
            {hasTrunkShortcut && (
              <SectionShortcutButton
                label="Komora bagażnika"
                targetId="stan-komory-bagaznika"
              />
            )}
          </div>
        </div>
      )}

      <div className="mt-[58px] w-full" id="podsumowanie-ekspertyzy-komory">
        <SectionSubtitle
          title="Podsumowanie ekspertyzy komory silnika i bagażnika"
          description="Ocena punktowa oraz zestawienie uwag."
        />

        <div className="flex flex-col gap-11 items-start mt-11 md:flex-row">
          {ratingSection.rating !== null ? (
            ratingSection.rating === 5 ?
              <CardSectionRateWide
                title={ratingSection.title}
                rating={ratingSection.rating}
                description={'Wygląda na to, że nasza ekspertyza nie wykazała żadnych nieprawidłowości w ocenie komory silnika i bagażnika.'}
                ratingMessage={ratingSection.ratingMessage}
              />
              : <>
                <CardSectionRate
                  title={ratingSection.title}
                  rating={ratingSection.rating}
                  description={ratingSection.description ?? undefined}
                  ratingMessage={ratingSection.ratingMessage}
                />

                {remarks.length > 0 && <TextSummaryBox title="Na to zwróć uwagę: " items={remarks} />}
              </>
          ) : (
            <SectionNoData
              title="Ocena komory silnika i bagażnika w przygotowaniu"
              description="Rzeczoznawca pracuje nad oceną komory silnika i bagażnika. Dane będą dostępne wkrótce."
            />
          )}
        </div>
      </div>

      {
        engineCompartmentItems.length > 0 && (
          <div className="mt-20" id="stan-komory-silnika">
            <SectionSubtitle
              title="Stan komory silnika"
              description="Weryfikacja komory silnika - sprawdzenie stanu i kompletności elementów."
            />
            <div className="mt-10">
              <InspectionList items={engineCompartmentItems} />

              {engineExpertSummary && (
                <div className="mt-20 md:mt-0 md:px-8">
                  <SectionSummary
                    iconName="handshake"
                    title="Podsumowanie Rzeczoznawcy dotyczące stanu komory silnika"
                    content={engineExpertSummary}
                    className="md:border-t-0 md:rounded-t-none"
                  />
                </div>
              )}
            </div>
          </div>
        )
      }

      {
        trunkCompartmentItems.length > 0 && (
          <div className="mt-20" id="stan-komory-bagaznika">
            <SectionSubtitle
              title="Stan komory bagażnika"
              description="Weryfikacja komory bagażnika – sprawdzenie stanu i wyposażenia przestrzeni bagażowej."
            />
            <div className="mt-10">
              <InspectionList items={trunkCompartmentItems} />

              {trunkExpertSummary && (
                <div className="mt-20 md:mt-0 md:px-8">
                  <SectionSummary
                    iconName="keyboard"
                    title=" Podsumowanie Rzeczoznawcy dotyczące stanu komory bagażnika"
                    content={trunkExpertSummary}
                    className="md:border-t-0 md:rounded-t-none"
                  />
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Galeria komory silnika */}
      {galleryPhotos && galleryPhotos.length > 0 && (
        <div id="galeria-komory" className="mt-12">
          <PhotoGallery
            photos={galleryPhotos}
            title="Galeria zdjęć komory silnika i bagażnika"
            iconName="handshake"
            collapsible={true}
            defaultExpanded={false}
            previewCount={4}
            galleryVariant="default"
          />
        </div>
      )}
    </>
  );
}
